import { useEffect, useState } from 'react';

/**
 * Реальная видимая область окна на телефоне.
 *
 * Зачем это вообще. Экранная клавиатура НЕ уменьшает ни `100vh`, ни `100dvh`:
 * dvh учитывает сворачивающуюся адресную строку браузера, но не клавиатуру.
 * Полноэкранный чат высотой `100dvh` остаётся во весь экран, клавиатура
 * ложится поверх — и нижний край вместе с полем ввода оказывается под ней.
 * Видно только шапку и пустое полотно, ровно как на скриншоте.
 *
 * Единственный источник правды тут — `window.visualViewport`: он и есть та
 * область, которая реально видна поверх клавиатуры.
 *
 *   height    — высота видимой области; ставим её чату вместо 100dvh, и
 *               шапка, лента и поле ввода снова помещаются целиком;
 *   offsetTop — насколько браузер сдвинул страницу вверх. iOS при фокусе
 *               подтягивает документ под клавиатуру, и без компенсации
 *               верх чата уезжает за край экрана.
 *
 * `null` означает «мерить нечем» — старый браузер или сервер. Вызывающий
 * в этом случае обязан откатиться на 100dvh: лучше прежнее поведение, чем
 * схлопнутая в ноль высота.
 */
export function useVisualViewport() {
  const [state, setState] = useState(() => read());

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    let frame = null;
    const onChange = () => {
      // Клавиатура выезжает анимацией, и события сыплются десятками за
      // несколько кадров. Через rAF пересчёт идёт не чаще кадра.
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setState(read());
      });
    };

    vv.addEventListener('resize', onChange);
    vv.addEventListener('scroll', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      vv.removeEventListener('resize', onChange);
      vv.removeEventListener('scroll', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  return state;
}

function read() {
  if (typeof window === 'undefined') return { height: null, offsetTop: 0, keyboard: 0 };
  const vv = window.visualViewport;
  if (!vv) return { height: null, offsetTop: 0, keyboard: 0 };
  return {
    height: Math.round(vv.height),
    offsetTop: Math.round(vv.offsetTop),
    // Сколько отъела клавиатура. Полезно не для вёрстки, а для решений
    // вроде «доскроллить ленту вниз, потому что клавиатура только что
    // открылась»: по одной высоте этого не понять.
    keyboard: Math.max(0, Math.round(window.innerHeight - vv.height)),
  };
}
